import React, { useState, useEffect } from "react"
import { mutationRequest } from "../../util/request"

interface FieldData {
  field: string
  name: string
  label: string
  placeholder: string
  required: boolean
  notes?: string
}

interface EditUserFormTextFieldProps {
  fieldData: FieldData
  value: string
  setValue: (value: string) => void
  userId?: number | null
  onSubmit?: (data: Record<string, any>) => Promise<any>
}

type ValidationState = "idle" | "waiting" | "validating" | "valid" | "invalid"

export const EditUserFormTextField: React.FC<EditUserFormTextFieldProps> = ({
  fieldData,
  value,
  setValue,
  userId = null,
  onSubmit = null,
}) => {
  const [initialValue] = useState(value)
  const [localVal, setLocalVal] = useState(value)
  const [validationState, setValidationState] = useState<ValidationState>(
    value ? "valid" : "idle",
  )
  const [validationError, setValidationError] = useState<string | null>(null)

  const setRemoteValue = (newVal: string) => {
    if (newVal === value) {
      setValidationState("valid")
    } else {
      setValidationState("waiting")
      setValue(newVal)
    }
  }

  const handleResponse = (response: {
    success: boolean
    errors?: { error: string }
  }) => {
    if (response.success) {
      setValidationState("valid")
      setValidationError(null)
    } else {
      setValidationState("invalid")
      setValidationError(response.errors?.error || null)
    }
  }

  useEffect(() => {
    if (value !== initialValue) {
      let updateData: Record<string, any> = {}
      updateData[fieldData.field] = value
      setValidationState("validating")
      if (onSubmit) {
        onSubmit(updateData).then(handleResponse)
      } else {
        handleUpdateUserData(userId, updateData).then(handleResponse)
      }
    }
  }, [userId, fieldData.field, value, initialValue, onSubmit])

  return (
    <div className="grid grid-cols-12 mt-10">
      <label
        className="block col-span-12 text-gray-900 text-xs uppercase font-bold"
        htmlFor={fieldData.name}
      >
        {fieldData.label}
      </label>
      <input
        className={`col-span-11 shadow appearance-none border rounded-sm w-full text-base py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
          validationState === "valid"
            ? "border-green-500"
            : validationState === "invalid"
              ? "border-red-500"
              : validationState === "validating"
                ? "border-yellow-500"
                : ""
        }`}
        type="text"
        name={fieldData.name}
        placeholder={fieldData.placeholder}
        required={fieldData.required}
        value={localVal}
        onFocus={(e) => setValidationState("waiting")}
        onChange={(e) => setLocalVal(e.target.value)}
        onBlur={(e) => setRemoteValue(localVal)}
      />
      <div className="col-span-1 pl-2 text-2xl">
        {validationState === "valid"
          ? "🤘"
          : validationState === "invalid"
            ? "👎"
            : validationState === "validating"
              ? "🤞"
              : validationState === "waiting"
                ? "🤔"
                : ""}
      </div>
      <div className="col-span-12 text-red-700 text-sm">{validationError}</div>
      {fieldData.notes && (
        <div className="col-span-12 pt-1 text-gray-600 text-sm">
          {fieldData.notes}
        </div>
      )}
    </div>
  )
}

const handleUpdateUserData = async (
  userId: number | null,
  userUpdateData: Record<string, any>,
) => {
  return mutationRequest({
    path: `/api/v1/users/${userId}`,
    options: {
      method: "PUT",
      body: JSON.stringify({
        user: userUpdateData,
      }),
    },
  })
}
